import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { surveyApi } from '@/services/adminApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Edit, Trash2, Play, Archive, ChevronDown, ChevronUp } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Survey, RoutingRule } from '@/types/admin';

const getQuestionTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    SINGLE_CHOICE: '⭕',
    MULTIPLE_CHOICE: '☑️',
    TEXT_SHORT: '📝',
    TEXT_LONG: '📄',
    NUMBER: '🔢',
    EMAIL: '📧',
    DATE: '📅',
    TIME: '⏰',
    RATING_SCALE: '⭐',
    SLIDER: '🎚️',
    DROPDOWN: '📋',
    FILE_UPLOAD: '📎',
    BOOLEAN: '✓',
    HEADING: '📌',
    BANNER: '🎉',
  };
  return icons[type] || '❓';
};

const formatQuestionType = (type: string): string => {
  return type.replace(/_/g, ' ');
};

export const SurveyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'survey');
  const canDelete = hasPermission('delete', 'survey');
  const canPublish = hasPermission('publish', 'survey');
  const canArchive = hasPermission('archive', 'survey');

  // Use entity hook for fetching survey
  const {
    data: survey,
    isLoading,
    error,
    refetch,
  } = useEntity<Survey>({
    fetchFn: (surveyId: string) => surveyApi.getById(surveyId),
    entityId: id,
    entityName: 'survey',
    enabled: !!id,
    onSuccess: (data) => {
      // Expand first question by default
      if (data.questions && data.questions.length > 0) {
        setExpandedQuestions(new Set([data.questions[0].id]));
      }
    },
    onError: () => {
      navigate('/admin/surveys');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Survey ID is required');
      await surveyApi.delete(id);
    },
    entityName: 'survey',
    operation: 'delete',
    onSuccess: () => {
      navigate('/admin/surveys');
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Survey ID is required');
      await surveyApi.publish(id);
    },
    entityName: 'survey',
    operation: 'update',
    successMessage: 'Survey published successfully',
    onSuccess: () => {
      refetch();
    },
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Survey ID is required');
      await surveyApi.archive(id);
    },
    entityName: 'survey',
    operation: 'update',
    successMessage: 'Survey archived successfully',
    onSuccess: () => {
      refetch();
    },
  });

  const toggleQuestion = (questionId: string) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this survey?')) return;
    await deleteMutation.mutate();
  };

  const handlePublish = async () => {
    if (!id) return;
    await publishMutation.mutate();
  };

  const handleArchive = async () => {
    if (!id) return;
    await archiveMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading survey..." />;
  }

  if (error || !survey) {
    return <ErrorState error={error || new Error('Survey not found')} onRetry={refetch} />;
  }

  const questionsCount = survey.questions?.length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/surveys">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Edit className="h-5 w-5" />
              View Survey
            </h2>
            <p className="text-muted-foreground mt-1">Viewing: {survey.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canPublish && survey.status === 'DRAFT' && (
            <Button onClick={handlePublish} disabled={publishMutation.isLoading}>
              <Play className="mr-2 h-4 w-4" />
              Publish
            </Button>
          )}
          {canArchive && survey.status !== 'ARCHIVED' && (
            <Button variant="outline" onClick={handleArchive} disabled={archiveMutation.isLoading}>
              <Archive className="mr-2 h-4 w-4" />
              Archive
            </Button>
          )}
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/surveys/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
          <TabsTrigger value="questions">
            Questions ({questionsCount})
          </TabsTrigger>
          <TabsTrigger value="configuration">Configuration</TabsTrigger>
        </TabsList>

        {/* Basic Information Tab */}
        <TabsContent value="basic" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Survey identification and classification</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <CopyableId id={survey.surveyId} label="Survey ID" />
                <div className="space-y-2">
                  <Label>Title</Label>
                  <p className="mt-1">{survey.title}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={survey.description || ''} disabled />
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Input value={survey.type} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Input value={survey.category} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        survey.status === 'PUBLISHED'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : survey.status === 'DRAFT'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {survey.status}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Active</Label>
                  <div>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        survey.active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {survey.active ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Audit Information */}
              <div className="pt-6 border-t">
                <p className="text-sm font-medium mb-4">Audit Information</p>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Created: </span>
                    <span>
                      {survey.createdAt
                        ? new Date(survey.createdAt).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated: </span>
                    <span>
                      {survey.updatedAt
                        ? new Date(survey.updatedAt).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Version: </span>
                    <span>{survey.version || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created By: </span>
                    <span>{survey.createdBy || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Modified By: </span>
                    <span>{survey.lastModifiedBy || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Questions Tab */}
        <TabsContent value="questions" className="space-y-6">
          {/* Survey-level routing rules */}
          {survey.routingRules && survey.routingRules.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Survey routing rules</CardTitle>
                <CardDescription>When condition is met, apply action (e.g. SKIP_TO, END_SURVEY)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {(survey.routingRules as RoutingRule[]).map((rule, rIdx) => (
                  <div
                    key={rIdx}
                    className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-muted/30 text-sm"
                  >
                    {rule.condition != null && (
                      <span className="font-mono text-muted-foreground break-all">{rule.condition}</span>
                    )}
                    <span className="px-1.5 py-0.5 rounded bg-muted font-medium">{rule.action}</span>
                    {rule.targetQuestionId != null && (
                      <span className="text-muted-foreground">→ Question: {rule.targetQuestionId}</span>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Questions ({questionsCount})</CardTitle>
              <CardDescription>Define the questions for your survey</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {questionsCount === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No questions added yet
                </div>
              ) : (
                survey.questions?.map((question, index) => {
                  const isExpanded = expandedQuestions.has(question.id);
                  return (
                    <div
                      key={question.id}
                      className="border rounded-lg overflow-hidden bg-card"
                    >
                      {/* Question Header */}
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <button
                              onClick={() => toggleQuestion(question.id)}
                              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="text-lg font-medium">
                                {getQuestionTypeIcon(question.type)}
                              </span>
                              <span className="text-lg font-medium">Q{index + 1}</span>
                            </button>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-muted-foreground">
                                  {formatQuestionType(question.type)}
                                </span>
                                {question.required && (
                                  <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                    Required
                                  </span>
                                )}
                              </div>
                              <p className="mt-1 font-medium">{question.text}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Question Details (Expanded) */}
                      {isExpanded && (
                        <div className="border-t bg-muted/30 p-4 space-y-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label>Question ID</Label>
                              <Input value={question.id} disabled className="font-mono text-sm" />
                            </div>
                            <div className="space-y-2">
                              <Label>Type</Label>
                              <Input value={formatQuestionType(question.type)} disabled />
                            </div>
                            {question.section && (
                              <div className="space-y-2">
                                <Label>Section</Label>
                                <Input
                                  value={JSON.stringify(question.section)}
                                  disabled
                                  className="text-sm"
                                />
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label>Question Text</Label>
                            <Input value={question.text} disabled />
                          </div>

                          {question.helpText && (
                            <div className="space-y-2">
                              <Label>Help Text</Label>
                              <Input value={question.helpText} disabled />
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-2 border-t">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm">Required:</Label>
                                <span className="text-sm">
                                  {question.required ? 'Yes' : 'No'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Label className="text-sm">Order:</Label>
                                <span className="text-sm">{question.order || index + 1}</span>
                              </div>
                            </div>
                          </div>

                          {/* Conditional rules (when to show/hide this question) */}
                          {question.conditionalRules && question.conditionalRules.length > 0 && (
                            <div className="space-y-2 pt-2 border-t">
                              <Label>Condition rules ({question.conditionalRules.length})</Label>
                              <div className="space-y-2">
                                {(question.conditionalRules as Array<{ condition?: string; action?: string; targetQuestionId?: string }>).map((rule, rIdx) => (
                                  <div
                                    key={rIdx}
                                    className="flex flex-wrap items-center gap-2 p-2 bg-background rounded border text-sm"
                                  >
                                    {rule.condition != null && (
                                      <span className="font-mono text-muted-foreground break-all">
                                        {String(rule.condition)}
                                      </span>
                                    )}
                                    {rule.action != null && (
                                      <span className="px-1.5 py-0.5 rounded bg-muted font-medium">
                                        → {String(rule.action)}
                                      </span>
                                    )}
                                    {rule.targetQuestionId != null && (
                                      <span className="text-muted-foreground">
                                        → Q: {String(rule.targetQuestionId)}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Options */}
                          {question.options && question.options.length > 0 && (
                            <div className="space-y-2 pt-2 border-t">
                              <Label>Options ({question.options.length})</Label>
                              <div className="space-y-2">
                                {question.options
                                  .sort((a, b) => a.order - b.order)
                                  .map((option) => (
                                    <div
                                      key={option.id}
                                      className="flex items-center gap-2 p-2 bg-background rounded border"
                                    >
                                      <span className="text-sm flex-1">{option.text}</span>
                                      {option.value && (
                                        <span className="text-xs text-muted-foreground">
                                          ({option.value})
                                        </span>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="configuration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
              <CardDescription>Configure survey behavior</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {survey.configuration ? (
                <>
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Allow Back Navigation</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow users to go back to previous questions
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      {survey.configuration.allowBackNavigation ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Show Progress Indicator</Label>
                      <p className="text-sm text-muted-foreground">
                        Display progress bar during survey
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      {survey.configuration.showProgressIndicator ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Randomize Questions</Label>
                      <p className="text-sm text-muted-foreground">
                        Show questions in random order
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      {survey.configuration.randomizeQuestions ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Randomize Options</Label>
                      <p className="text-sm text-muted-foreground">
                        Show options in random order
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      {survey.configuration.randomizeOptions ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Auto Save</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically save responses
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      {survey.configuration.autoSave ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Allow Multiple Responses</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow users to submit multiple responses
                      </p>
                    </div>
                    <span className="text-sm font-medium">
                      {survey.configuration.allowMultipleResponses ? 'Yes' : 'No'}
                    </span>
                  </div>

                  {survey.configuration.timeLimit && (
                    <div className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <Label className="text-base">Time Limit</Label>
                        <p className="text-sm text-muted-foreground">
                          Maximum time allowed to complete survey
                        </p>
                      </div>
                      <span className="text-sm font-medium">
                        {survey.configuration.timeLimit} seconds
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No configuration set
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SurveyDetailPage;
